/**
 * useGameKill - Order cancellation with granular control:
 *
 * 1. killTickers(tickers)           - Cancel ALL orders for specific tickers
 * 2. killPartial(tickers, percent)  - Cancel N% of orders, worst-first
 *
 * Neither Kalshi nor Polymarket supports batch cancel by ticker/event,
 * so all kills loop individual cancelOrder calls with progress tracking.
 */

import { useState, useCallback, useRef } from 'react';
import type { KalshiOrder } from '@/lib/kalshiApi';

export interface KillProgress {
  done: number;
  total: number;
}

export interface GameKillActions {
  killTickers: (tickers: string[]) => Promise<void>;
  killPartial: (tickers: string[], percent: number) => Promise<void>;
  isKilling: boolean;
  killProgress: KillProgress | null;
}

/**
 * Sort orders so the "worst" (furthest from mid-market) are cancelled first.
 * Uses price as heuristic -- orders closer to extremes (0 or 100) go first.
 */
function sortWorstFirst(orders: KalshiOrder[]): KalshiOrder[] {
  return [...orders].sort((a, b) => {
    const priceA = a.side === 'yes' ? (a.yes_price ?? 50) : (a.no_price ?? 50);
    const priceB = b.side === 'yes' ? (b.yes_price ?? 50) : (b.no_price ?? 50);
    const distA = Math.abs(priceA - 50);
    const distB = Math.abs(priceB - 50);
    return distB - distA;
  });
}

export function useGameKill(
  orders: KalshiOrder[],
  onCancelOrder: (orderId: string) => Promise<void>
): GameKillActions {
  const [isKilling, setIsKilling] = useState(false);
  const [killProgress, setKillProgress] = useState<KillProgress | null>(null);
  const abortRef = useRef(false);

  const cancelOrders = useCallback(
    async (toCancel: KalshiOrder[]) => {
      if (toCancel.length === 0) return;

      abortRef.current = false;
      setIsKilling(true);
      setKillProgress({ done: 0, total: toCancel.length });

      let done = 0;
      for (const order of toCancel) {
        if (abortRef.current) break;
        try {
          await onCancelOrder(order.order_id);
        } catch (err) {
          console.warn(`Failed to cancel order ${order.order_id}:`, err);
        }
        done += 1;
        setKillProgress({ done, total: toCancel.length });
      }

      setIsKilling(false);
      setKillProgress(null);
    },
    [onCancelOrder]
  );

  const getActiveOrdersForTickers = useCallback(
    (tickers: string[]): KalshiOrder[] => {
      const tickerSet = new Set(tickers);
      return orders.filter(
        (o) => tickerSet.has(o.ticker) && (o.status === 'resting' || o.status === 'pending')
      );
    },
    [orders]
  );

  const killTickers = useCallback(
    async (tickers: string[]) => {
      const toCancel = getActiveOrdersForTickers(tickers);
      await cancelOrders(toCancel);
    },
    [getActiveOrdersForTickers, cancelOrders]
  );

  const killPartial = useCallback(
    async (tickers: string[], percent: number) => {
      const allOrders = getActiveOrdersForTickers(tickers);
      const sorted = sortWorstFirst(allOrders);
      const count = Math.max(1, Math.ceil(sorted.length * (percent / 100)));
      const toCancel = sorted.slice(0, count);
      await cancelOrders(toCancel);
    },
    [getActiveOrdersForTickers, cancelOrders]
  );

  return {
    killTickers,
    killPartial,
    isKilling,
    killProgress,
  };
}
